import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import ChequeLienzo from './ChequeLienzo';

const CAMPOS = [
  { grupo: 'Fecha', x: 'fecha_x', y: 'fecha_y' },
  { grupo: 'Nombre beneficiario', x: 'nombre_x', y: 'nombre_y' },
  { grupo: 'Monto en números', x: 'monto_x', y: 'monto_y' },
  { grupo: 'Cantidad en letras', x: 'texto_x', y: 'texto_y' },
];

const NUEVO_BANCO_INICIAL = { nombre: '', ancho_mm: 155, alto_mm: 75 };

export default function Calibracion() {
  const [bancos, setBancos] = useState([]);
  const [bancoId, setBancoId] = useState(null);
  const [form, setForm] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const [mostrarNuevoBanco, setMostrarNuevoBanco] = useState(false);
  const [nuevoBanco, setNuevoBanco] = useState(NUEVO_BANCO_INICIAL);
  const [creandoBanco, setCreandoBanco] = useState(false);

  function cargarBancos(seleccionarId) {
    return api.get('/bancos/').then((res) => {
      setBancos(res.data);
      if (seleccionarId) {
        setBancoId(seleccionarId);
      } else if (res.data.length > 0 && !bancoId) {
        setBancoId(res.data[0].id);
      }
      return res.data;
    });
  }

  useEffect(() => {
    cargarBancos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!bancoId) return;
    api.get(`/bancos/${bancoId}/plantilla/`).then((res) => setForm(res.data));
  }, [bancoId]);

  const banco = bancos.find((b) => b.id === bancoId);

  function setCampo(campo, valor) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  async function handleGuardar(e) {
    e.preventDefault();
    setGuardando(true);
    try {
      const res = await api.put(`/bancos/${bancoId}/plantilla/`, {
        fecha_x: form.fecha_x, fecha_y: form.fecha_y,
        nombre_x: form.nombre_x, nombre_y: form.nombre_y,
        monto_x: form.monto_x, monto_y: form.monto_y,
        texto_x: form.texto_x, texto_y: form.texto_y, texto_w: form.texto_w,
        fuente_pt: form.fuente_pt, relleno_char: form.relleno_char,
      });
      setForm(res.data);
      toast.success('Calibración guardada para tu empresa.');
    } catch {
      toast.error('No se pudo guardar la calibración.');
    } finally {
      setGuardando(false);
    }
  }

  async function handleCrearBanco(e) {
    e.preventDefault();
    if (!nuevoBanco.nombre.trim()) return;
    setCreandoBanco(true);
    try {
      const res = await api.post('/bancos/', nuevoBanco);
      await cargarBancos(res.data.id);
      setMostrarNuevoBanco(false);
      setNuevoBanco(NUEVO_BANCO_INICIAL);
      toast.success(`${res.data.nombre} agregado. Ahora puedes calibrarlo.`);
    } catch (err) {
      const detalle = err.response?.data;
      const mensaje = detalle ? Object.values(detalle).flat().join(' ') : 'No se pudo crear el banco.';
      toast.error(mensaje);
    } finally {
      setCreandoBanco(false);
    }
  }

  return (
    <div className="generador">
      <div className="panel-controles no-imprimir">
        <div className="control-group">
          <label>Banco</label>
          <select value={bancoId || ''} onChange={(e) => setBancoId(Number(e.target.value))}>
            {bancos.map((b) => (
              <option key={b.id} value={b.id}>{b.nombre}</option>
            ))}
          </select>

          {!mostrarNuevoBanco ? (
            <button type="button" className="btn-enlace" onClick={() => setMostrarNuevoBanco(true)}>
              <Plus size={14} /> Agregar un banco que no está en la lista
            </button>
          ) : (
            <form className="nuevo-banco" onSubmit={handleCrearBanco}>
              <label>Nombre del banco</label>
              <input
                value={nuevoBanco.nombre}
                onChange={(e) => setNuevoBanco((b) => ({ ...b, nombre: e.target.value }))}
                placeholder="Ej. Banco Pichincha"
                required
              />
              <div className="coord-inputs">
                <div>
                  <label>Ancho (mm)</label>
                  <input type="number" step="1" value={nuevoBanco.ancho_mm} onChange={(e) => setNuevoBanco((b) => ({ ...b, ancho_mm: e.target.value }))} />
                </div>
                <div>
                  <label>Alto (mm)</label>
                  <input type="number" step="1" value={nuevoBanco.alto_mm} onChange={(e) => setNuevoBanco((b) => ({ ...b, alto_mm: e.target.value }))} />
                </div>
              </div>
              <p className="ayuda">Se crea con una calibración de referencia genérica que después ajustas abajo.</p>
              <div className="acciones-nuevo-banco">
                <button type="button" className="btn-secundario" onClick={() => setMostrarNuevoBanco(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" disabled={creandoBanco}>
                  {creandoBanco ? 'Creando…' : 'Crear banco'}
                </button>
              </div>
            </form>
          )}

          <div className="coord-inputs">
            <div>
              <label>Relleno</label>
              <select value={form?.relleno_char ?? ''} onChange={(e) => setCampo('relleno_char', e.target.value)}>
                <option value="*">Asteriscos (*)</option>
                <option value="-">Guiones (-)</option>
                <option value="">Ninguno</option>
              </select>
            </div>
            <div>
              <label>Tamaño letra (pt)</label>
              <input type="number" step="0.5" value={form?.fuente_pt ?? ''} onChange={(e) => setCampo('fuente_pt', e.target.value)} />
            </div>
          </div>
        </div>

        {form && (
          <div className="control-group">
            <h4>Calibración fina</h4>
            <p className="ayuda">Ajusta X (izquierda) e Y (arriba), en milímetros, hasta que coincida con tu talonario físico.</p>

            {CAMPOS.map(({ grupo, x, y }) => (
              <div key={grupo}>
                <label>{grupo}</label>
                <div className="coord-inputs">
                  <div>X (mm): <input type="number" step="1" value={form[x]} onChange={(e) => setCampo(x, e.target.value)} /></div>
                  <div>Y (mm): <input type="number" step="1" value={form[y]} onChange={(e) => setCampo(y, e.target.value)} /></div>
                </div>
              </div>
            ))}

            <label>Ancho de línea del texto (mm)</label>
            <input type="number" step="1" value={form.texto_w} onChange={(e) => setCampo('texto_w', e.target.value)} />
          </div>
        )}

        <button className="btn-primary" onClick={handleGuardar} disabled={!form || guardando}>
          {guardando ? 'Guardando…' : 'Guardar calibración'}
        </button>
      </div>

      <div className="preview-area no-imprimir-fondo">
        <ChequeLienzo
          banco={banco}
          calibracion={form}
          fecha="Guayaquil 2026-01-01"
          nombre="NOMBRE DE EJEMPLO"
          montoFormateado="1,234.56"
          montoTexto="MIL DOSCIENTOS TREINTA Y CUATRO CON 56/100 DÓLARES ****************"
        />
      </div>
    </div>
  );
}
